import _ from 'lodash';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { FormGroup } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormInputLabel, FormInputHelp, FormInputToolbar, FormInputIcon, FormContext, skipUpdate } from 'blaze-form';
import { LIST_ITEM_DELIMITER } from '../../../util/form';
import '../../../styles/form/search.scss';

RegExp.escape = function (s = '') {
  if (typeof s !== 'string') {
    return '';
  }
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); // eslint-disable-line
};

const hasSuggestion = function (s = '', type) {
  if (type === 'zipcode') {
    return true;
  }

  const regex = new RegExp(`^(.*):${LIST_ITEM_DELIMITER}`, 'u');
  return regex.test(s);
};

const FormInputSearchItem = React.memo(props => {
  const { field = {}, controlId, disabled, noLabel, touched, originalValue, search, placeholder } = props;

  const consumerUpdate = useRef(false);
  const firstUpdate = useRef(true);
  const blurSetValue = useRef(false);
  const controlIcon = useRef(null);

  const [value, setValue] = useState(props.value);
  const [valid, setValid] = useState(props.valid);
  const [errorMessage, setErrorMessage] = useState(props.errorMessage);
  const [dirty, setDirty] = useState(!_.isEqual(props.value, props.originalValue));
  const [warning, setWarning] = useState(props.warning);
  const [info, setInfo] = useState(props.info);
  const [list, setList] = useState(_.get(props.search, 'list'));
  const [loading, setLoading] = useState(false);
  const [validationKeyValue, setValidationKeyValue] = useState(_.get(props.search, 'keyValue'));

  const { ee, trackDirty } = useContext(FormContext);

  // this.stopLoading = _.debounce(this.stopLoading.bind(this), 750);

  const clear = () => {
    props.onChange(field.name, '');
    setDirty(false);
    setErrorMessage('');
    setValid(true);
    setValue('');
    setWarning('');
  };

  const consume = ({ action, payload: data }) => {
    if (action === 'clear') {
      clear();
      return;
    }

    if (action === 'sync') {
      if (search.key && data[search.key]) {
        setValidationKeyValue(data[search.key].data);
      }

      return;
    }

    if (action !== 'update' || !data) {
      return;
    }

    if (data.list !== undefined) {
      setList(data.list);
    }

    if (data.value !== undefined) {
      consumerUpdate.current = true;
      setValue(data.value);
    }

    if (data.options !== undefined) {
      setList(data.options);
    }

    if (data.warning !== undefined) {
      setWarning(data.warning);
    }

    if (data.info !== undefined) {
      setInfo(data.info);
    }
  };

  const getSuggestionValue = suggestion => {
    if (!suggestion) {
      return '';
    }

    if (suggestion && typeof suggestion !== 'object') {
      return suggestion;
    }

    const suggestionKeys = Object.keys(suggestion);
    const itemid = suggestionKeys[0];
    const field = suggestion.Name ? 'Name' : suggestionKeys[1];

    if (!field || !suggestion[field]) {
      return suggestion.itemid;
    }

    return `${suggestion[itemid]}:${LIST_ITEM_DELIMITER}${suggestion[field]}`;
  };

  const onBlur = suggestion => {
    let newValue = value;

    if (suggestion) {
      newValue = getSuggestionValue(suggestion);
    }

    if (!search.optional && !suggestion && !hasSuggestion(value, field.type)) {
      if (originalValue !== '' && newValue === originalValue) {
        // Original Value check added so that user can quick tab through an existing record without
        // unwanted changes.  This only happens in tables as the values aren't in 'suggestion' format (TODO?)
      } else {
        newValue = '';
      }
    }

    if (newValue !== value) {
      blurSetValue.current = true;
      setValue(newValue);
    }

    if ((search.optional && !suggestion) || !blurSetValue.current) {
      const result = props.onBlur(field.name);

      if (result) {
        setValid(result.valid !== undefined ? result.valid : valid);
        setErrorMessage(result.validError !== undefined ? result.validError : errorMessage);
        return true;
      }
    }

    return false;
  };

  const onSuggestionSelected = (suggestion, fullSuggestion) => {
    const newValue = getSuggestionValue(suggestion);

    if (props.onSuggestionSelected) {
      props.onSuggestionSelected(suggestion, newValue);
    }

    if (props.onFullSuggestionSelected) {
      props.onFullSuggestionSelected(fullSuggestion);
    }

    if (!props.touched) {
      onBlur(newValue);
      return;
    }

    if (newValue !== value) {
      blurSetValue.current = true;
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const re = new RegExp(`^${RegExp.escape(originalValue)}:`);
    const isDirty = !_.isEqual(value, originalValue) && !re.test(value);

    setDirty(isDirty);
    props.onChange(field.name, value);

    if (consumerUpdate.current) {
      consumerUpdate.current = false;
      onBlur(value);
    }

    if (blurSetValue.current) {
      blurSetValue.current = false;
      const result = props.onBlur(field.name);

      if (result) {
        setValid(result.valid !== undefined ? result.valid : valid);
        setErrorMessage(result.validError !== undefined ? result.validError : errorMessage);
      }
    }
  }, [value]);

  // Original Value changed from props (usually a submit)
  useEffect(() => {
    if (!_.isEqual(originalValue, value)) {
      setDirty(false);
    }
  }, [originalValue]);

  // Validation state changed from props (usually a submit)
  useEffect(() => {
    if (props.valid !== valid) {
      setValid(props.valid);
      setErrorMessage(props.errorMessage);
    }
  }, [props.valid]);

  useEffect(() => {
    if (ee) {
      ee.on('syncForm', (...args) => {
        setTimeout(() => {
          consume(...args);
        }, 10);
      });

      ee.on(controlId, (...args) => {
        setTimeout(() => {
          consume(...args);
        }, 10);
      });
    }

    return () => {
      if (ee) {
        ee.off('syncForm');
        ee.off(controlId);
      }
    };
  }, []);

  useEffect(() => {
    // Make sure this flag is cleared every render
    consumerUpdate.current = false;
  });

  const SearchComponent = props.searchComponent;

  const currentValue = Array.isArray(value) ? '' : value;
  const additionalProps = {};
  const icon = 'search';

  const isInvalid = !valid && touched && !field.readOnly;
  const isDirty = dirty && valid && !field.readOnly && trackDirty;
  const isReadOnly = field.readOnly || disabled;

  const containerClass = classNames({
    'has-icon': !!icon && !isReadOnly,
    'is-enabled': !isReadOnly,
    'is-invalid': isInvalid,
    'is-dirty': isDirty,
    'is-readonly': isReadOnly,
    'has-suggestion': !!currentValue,
  });

  // TODO this should ONLY contain common search props
  const searchProps = {
    ...search,
    clearValue: () => setValue(''),
    command: search.command,
    controlId,
    disabled,
    filter: search.filter,
    highlightFirstSuggestion: !search.optional,
    initValue: !field.collection,
    limit: search.limit,
    list,
    loading,
    localSearch: search.local || false,
    onBlur,
    onChange: v => setValue(v),
    onSuggestionSelected,
    optional: search.optional,
    pageName: search.page,
    placeholder: isReadOnly ? '' : placeholder || 'Search <?>',
    readOnly: isReadOnly,
    setLoading,
    sort: search.sort,
    url: search.url,
    validationKeyValue,
    value: currentValue,
    ...props.searchProps,
  };

  // TODO Probably shouldn't live here
  if (search.modelName === 'loyaltycustomer' && validationKeyValue) {
    searchProps.serverFilter = { customerNum: validationKeyValue.toLowerCase() };
  }

  return (
    <FormGroup
      id={`${controlId}-control`}
      key={controlId}
      controlId={controlId}
      className={containerClass}
      {...additionalProps}
    >
      <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
      <div className="search-item-container">
        <SearchComponent {...searchProps} />
        <FormInputToolbar
          toolbar={_.get(field, 'toolbar')}
          setValue={v => setValue(v)}
          blurValue={onBlur}
          disabled={!currentValue}
          value={currentValue}
        />
        {currentValue !== '' && (
          <div className="clear-icon" title="Clear" onClick={() => setValue('')}>
            <i className="fal fa-times fa-fw" />
          </div>
        )}
        {loading && <div className="line-loader" />}
      </div>
      {isReadOnly === false && <FormInputIcon icon={icon} ref={controlIcon} />}
      {warning && <div className="warning-block">{warning}</div>}
      {info && <div className="info-block">{info}</div>}
      <FormInputHelp errorText={errorMessage}>{field.helpText}</FormInputHelp>
    </FormGroup>
  );
}, skipUpdate);

FormInputSearchItem.defaultProps = {
  disabled: false,
  errorMessage: '',
  noLabel: false,
  onBlur: () => {},
  onChange: () => {},
  search: {},
  searchProps: {},
  valid: true,
  value: '',
};

FormInputSearchItem.propTypes = {
  controlId: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  info: PropTypes.string,
  noLabel: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  originalValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  search: PropTypes.object,
  searchComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  searchProps: PropTypes.object,
  setWarning: PropTypes.func,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  warning: PropTypes.string,
};

export default FormInputSearchItem;
